import { useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import {
  MOCK_IN_RUN_CREWING,
  MOCK_CHANGEOVER_CREWING,
  MOCK_BPM_DATA,
  MOCK_PRODUCTS,
  MOCK_CALENDAR,
  MOCK_CHANGEOVER_EVENTS,
  MOCK_JOB_HISTORY,
  MOCK_HOLIDAYS,
  generateForecast,
  type MockHoliday,
} from '../mock/mockData';
import CPMockBadge from './CPMockBadge';
import styles from './cpReferenceTables.module.scss';

// ---------------------------------------------------------------------------
// In-Run Crewing
// ---------------------------------------------------------------------------
const IN_RUN_ROOM_TYPES = [
  'High Speed 1',
  'High Speed 2',
  'Medium Speed',
  'Low Speed 1',
  'Low Speed 2',
  'Overlay',
  'SAPS',
] as const;
type InRunRoomType = (typeof IN_RUN_ROOM_TYPES)[number];

// ---------------------------------------------------------------------------
// Changeover Crewing — expanded to individual room types
// ---------------------------------------------------------------------------
const CO_CREWING_ROWS = (
  ['L1', 'L2', 'L3'] as const
).flatMap((level) => [
  { label: `${level} High Speed 1`, key: `${level}_High Speed 1` },
  { label: `${level} High Speed 2`, key: `${level}_High Speed 2` },
  { label: `${level} Medium Speed`, key: `${level}_Medium Speed` },
  { label: `${level} Low Speed 1`,  key: `${level}_Low Speed 1`  },
  { label: `${level} Low Speed 2`,  key: `${level}_Low Speed 2`  },
  { label: `${level} Overlay`,      key: `${level}_Overlay`      },
]);

// ---------------------------------------------------------------------------
// BPM — first 50 rows
// ---------------------------------------------------------------------------
const BPM_PREVIEW = MOCK_BPM_DATA.slice(0, 50);

// ---------------------------------------------------------------------------
// Forecast pivot: 29 NDC rows × 12 month columns
// ---------------------------------------------------------------------------
const NDC_DESC: Record<string, string> = {};
MOCK_PRODUCTS.forEach((p) => { NDC_DESC[p.NDC11] = p.Description; });

function buildForecastPivot(): Record<string, unknown>[] {
  const rows = generateForecast();
  const map: Record<string, Record<string, unknown>> = {};
  rows.forEach((row) => {
    if (!map[row.NDC11]) {
      map[row.NDC11] = { NDC11: row.NDC11, Description: NDC_DESC[row.NDC11] ?? '' };
    }
    const [yr, mn] = row.ForecastPeriod.split('-');
    const label = new Date(Number(yr), Number(mn) - 1, 1)
      .toLocaleString('default', { month: 'short', year: '2-digit' });
    map[row.NDC11][label] = row.ForecastBottles;
  });
  return Object.values(map);
}
const FORECAST_PIVOT = buildForecastPivot();
const FORECAST_MONTH_COLS = MOCK_CALENDAR.map((c) => {
  const [yr, mn] = c.month.split('-');
  return new Date(Number(yr), Number(mn) - 1, 1)
    .toLocaleString('default', { month: 'short', year: '2-digit' });
});

// ---------------------------------------------------------------------------
// Job History — formatted rows for the DOR Paste table
// ---------------------------------------------------------------------------
const JOB_HISTORY_ROWS = MOCK_JOB_HISTORY.map((r) => ({
  date:     new Date(r.yr, r.mth - 1, 1).toLocaleString('default', { month: 'short', year: 'numeric' }),
  jobs:     r.jobs,
  bottles:  r.total_bottles.toLocaleString(),
  pills:    r.total_pills.toLocaleString(),
}));

// ---------------------------------------------------------------------------
// Shared row type for both crewing tables
// ---------------------------------------------------------------------------
type CrewingRow = { PA: number; MO: number; QI: number };

// ---------------------------------------------------------------------------
// CPReferenceTables
// ---------------------------------------------------------------------------
const CPReferenceTables = observer(() => {
  // In-run crewing state — keyed by room type name
  const [inRunEdit, setInRunEdit] = useState<Record<InRunRoomType, CrewingRow>>(
    () =>
      Object.fromEntries(
        IN_RUN_ROOM_TYPES.map((rt) => [
          rt,
          { ...(MOCK_IN_RUN_CREWING[rt] ?? { PA: 1, MO: 1, QI: 1 }) },
        ]),
      ) as Record<InRunRoomType, CrewingRow>,
  );
  const [inRunEditing, setInRunEditing] = useState(false);
  const [inRunRaw, setInRunRaw] = useState<Record<string, string>>({});

  // Changeover crewing state — keyed by `${level}_${roomType}`
  const [coEdit, setCoEdit] = useState<Record<string, CrewingRow>>(
    () =>
      Object.fromEntries(
        CO_CREWING_ROWS.map(({ key }) => [
          key,
          { ...(MOCK_CHANGEOVER_CREWING[key] ?? { PA: 1, MO: 1, QI: 1 }) },
        ]),
      ),
  );
  const [coEditing, setCoEditing] = useState(false);
  const [coRaw, setCoRaw] = useState<Record<string, string>>({});

  // Holiday state
  const [holidays, setHolidays]       = useState<MockHoliday[]>(() => MOCK_HOLIDAYS.map((h) => ({ ...h })));
  const [holidayEditing, setHolidayEditing] = useState(false);
  const [holidayOriginal, setHolidayOriginal] = useState<MockHoliday[]>(() => MOCK_HOLIDAYS.map((h) => ({ ...h })));
  const holidayIdCounter = useRef(MOCK_HOLIDAYS.length + 1);

  const handleHolidayChange = (id: string, field: keyof MockHoliday, value: string) => {
    setHolidays((prev) => prev.map((h) => h.id === id ? { ...h, [field]: value } : h));
  };

  const handleHolidayAdd = () => {
    const newId = `hn${holidayIdCounter.current++}`;
    setHolidays((prev) => [...prev, { id: newId, date: '', name: '' }]);
  };

  const handleHolidayDelete = (id: string) => {
    setHolidays((prev) => prev.filter((h) => h.id !== id));
  };

  const handleHolidaySave = () => {
    setHolidayOriginal(holidays.map((h) => ({ ...h })));
    setHolidayEditing(false);
    toast.info('Changes are visual only in demo mode.', { autoClose: 3000 });
  };

  const handleHolidayCancel = () => {
    setHolidays(holidayOriginal.map((h) => ({ ...h })));
    setHolidayEditing(false);
  };

  const handleInRunChange = (cellId: string, raw: string) => {
    if (!/^(\d*\.?\d*)$/.test(raw)) return;
    setInRunRaw((prev) => ({ ...prev, [cellId]: raw }));
  };

  const handleInRunBlur = (cellId: string, rt: InRunRoomType, role: keyof CrewingRow) => {
    const raw = inRunRaw[cellId] ?? '';
    const v = parseFloat(raw);
    const val = Number.isNaN(v) ? 0 : v;
    setInRunEdit((prev) => ({ ...prev, [rt]: { ...prev[rt], [role]: val } }));
    setInRunRaw((prev) => ({ ...prev, [cellId]: String(val) }));
  };

  const handleCoChange = (cellId: string, raw: string) => {
    if (!/^(\d*\.?\d*)$/.test(raw)) return;
    setCoRaw((prev) => ({ ...prev, [cellId]: raw }));
  };

  const handleCoBlur = (cellId: string, key: string, role: keyof CrewingRow) => {
    const raw = coRaw[cellId] ?? '';
    const v = parseFloat(raw);
    const val = Number.isNaN(v) ? 0 : v;
    setCoEdit((prev) => ({ ...prev, [key]: { ...prev[key], [role]: val } }));
    setCoRaw((prev) => ({ ...prev, [cellId]: String(val) }));
  };

  const handleSave = () => {
    toast.info('Changes are visual only in demo mode.', { autoClose: 3000 });
  };

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <i className="pi pi-list" />
        <h2>Reference Tables</h2>
        <CPMockBadge />
      </div>

      <div className={styles.tabContainer}>
        <TabView className={styles.tabView}>

          {/* ================================================================ */}
          {/* Tab 1 — Product Master                                           */}
          {/* ================================================================ */}
          <TabPanel header="Product Master" leftIcon="pi pi-box mr-2">
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                value={MOCK_PRODUCTS}
                size="small"
                stripedRows
                showGridlines
                paginator
                rows={15}
                emptyMessage="No products."
              >
                <Column field="NDC11"       header="NDC11"       sortable style={{ minWidth: '150px' }} />
                <Column field="StockCode"   header="Stock Code"  sortable style={{ minWidth: '110px' }} />
                <Column field="Description" header="Description" sortable style={{ minWidth: '200px' }} />
                <Column field="JobType"     header="Job Type"    sortable style={{ minWidth: '110px' }} />
                <Column field="RoomName"    header="Room"        sortable style={{ minWidth: '110px' }} />
                <Column field="RoomTypeId"  header="Room Type ID" sortable style={{ minWidth: '120px' }} />
                <Column field="BPM"         header="BPM"         sortable style={{ minWidth: '80px' }} />
                <Column field="Ebq"         header="EBQ"         sortable style={{ minWidth: '80px' }} />
              </DataTable>
            </div>
          </TabPanel>

          {/* ================================================================ */}
          {/* Tab 2 — Forecast (NDC × months pivot)                            */}
          {/* ================================================================ */}
          <TabPanel header="Forecast" leftIcon="pi pi-chart-bar mr-2">
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                value={FORECAST_PIVOT}
                size="small"
                stripedRows
                showGridlines
                scrollable
                scrollHeight="520px"
                emptyMessage="No forecast data."
              >
                <Column field="NDC11"       header="NDC11"       sortable style={{ minWidth: '150px', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1 }} />
                <Column field="Description" header="Description" sortable style={{ minWidth: '180px' }} />
                {FORECAST_MONTH_COLS.map((col) => (
                  <Column
                    key={col}
                    field={col}
                    header={col}
                    sortable
                    style={{ minWidth: '80px', textAlign: 'right' }}
                    body={(row: Record<string, unknown>) =>
                      typeof row[col] === 'number'
                        ? (row[col] as number).toLocaleString()
                        : '--'
                    }
                  />
                ))}
              </DataTable>
            </div>
          </TabPanel>

          {/* ================================================================ */}
          {/* Tab 3 — Job History (DOR Paste)                                  */}
          {/* ================================================================ */}
          <TabPanel header="Job History" leftIcon="pi pi-history mr-2">
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                value={JOB_HISTORY_ROWS}
                size="small"
                stripedRows
                showGridlines
                emptyMessage="No job history."
              >
                <Column field="date"    header="Date"          sortable style={{ minWidth: '120px' }} />
                <Column field="jobs"    header="Jobs"          sortable style={{ minWidth: '80px' }} />
                <Column field="bottles" header="Total Bottles" sortable style={{ minWidth: '140px' }} />
                <Column field="pills"   header="Total Pills"   sortable style={{ minWidth: '140px' }} />
              </DataTable>
            </div>
          </TabPanel>

          {/* ================================================================ */}
          {/* Tab 4 — Changeovers                                              */}
          {/* ================================================================ */}
          <TabPanel header="Changeovers" leftIcon="pi pi-replay mr-2">
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                value={MOCK_CHANGEOVER_EVENTS}
                size="small"
                stripedRows
                showGridlines
                paginator
                rows={20}
                emptyMessage="No changeover data."
              >
                <Column field="date"       header="Date"                  sortable style={{ minWidth: '110px' }} />
                <Column field="shift"      header="Shift"                 sortable style={{ minWidth: '90px' }} />
                <Column field="room"       header="Room"                  sortable style={{ minWidth: '90px' }} />
                <Column field="clean_type" header="Type of Clean"         sortable style={{ minWidth: '120px' }} />
                <Column
                  field="r2_minutes"
                  header="Changeover R2 (mins)"
                  sortable
                  style={{ minWidth: '160px' }}
                  body={(r) => r.r2_minutes.toFixed(1)}
                />
                <Column field="copaste"    header="Clean Code"            sortable style={{ minWidth: '110px' }} />
              </DataTable>
            </div>
          </TabPanel>

          {/* ================================================================ */}
          {/* Tab 5 — In-Run Crewing                                           */}
          {/* ================================================================ */}
          <TabPanel header="In-Run Crewing" leftIcon="pi pi-users mr-2">
            <div className={styles.saveBar} style={{ justifyContent: 'flex-end' }}>
              {inRunEditing ? (
                <>
                  <Button
                    label="Save"
                    icon="pi pi-check"
                    size="small"
                    onClick={() => { handleSave(); setInRunEditing(false); }}
                  />
                  <Button
                    label="Cancel"
                    icon="pi pi-times"
                    size="small"
                    severity="secondary"
                    onClick={() => {
                      setInRunRaw({});
                      setInRunEdit(
                        Object.fromEntries(
                          IN_RUN_ROOM_TYPES.map((rt) => [
                            rt,
                            { ...(MOCK_IN_RUN_CREWING[rt] ?? { PA: 1, MO: 1, QI: 1 }) },
                          ]),
                        ) as Record<InRunRoomType, CrewingRow>,
                      );
                      setInRunEditing(false);
                    }}
                  />
                </>
              ) : (
                <Button
                  label="Edit"
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  onClick={() => {
                    const raw: Record<string, string> = {};
                    IN_RUN_ROOM_TYPES.forEach((rt) => {
                      (['PA', 'MO', 'QI'] as const).forEach((role) => {
                        raw[`${rt}_${role}`] = String(inRunEdit[rt]?.[role] ?? 0);
                      });
                    });
                    setInRunRaw(raw);
                    setInRunEditing(true);
                  }}
                />
              )}
            </div>
            <div className={styles.tableScrollWrapper}>
            <table className={styles.simpleTable}>
              <thead>
                <tr>
                  <th>Room Type</th>
                  <th>PA</th>
                  <th>MO</th>
                  <th>QI</th>
                </tr>
              </thead>
              <tbody>
                {IN_RUN_ROOM_TYPES.map((rt) => (
                  <tr key={rt}>
                    <td><strong>{rt}</strong></td>
                    {(['PA', 'MO', 'QI'] as const).map((role) => (
                      <td key={role}>
                        {inRunEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            className={styles.editInput}
                            value={inRunRaw[`${rt}_${role}`] ?? ''}
                            onChange={(e) => handleInRunChange(`${rt}_${role}`, e.target.value)}
                            onBlur={() => handleInRunBlur(`${rt}_${role}`, rt, role)}
                            onFocus={() => { if (inRunRaw[`${rt}_${role}`] === '0') setInRunRaw((prev) => ({ ...prev, [`${rt}_${role}`]: '' })); }}
                          />
                        ) : (
                          inRunEdit[rt][role]
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </TabPanel>

          {/* ================================================================ */}
          {/* Tab 6 — Changeover Crewing                                       */}
          {/* ================================================================ */}
          <TabPanel header="Changeover Crewing" leftIcon="pi pi-refresh mr-2">
            <div className={styles.saveBar} style={{ justifyContent: 'flex-end' }}>
              {coEditing ? (
                <>
                  <Button
                    label="Save"
                    icon="pi pi-check"
                    size="small"
                    onClick={() => { handleSave(); setCoEditing(false); }}
                  />
                  <Button
                    label="Cancel"
                    icon="pi pi-times"
                    size="small"
                    severity="secondary"
                    onClick={() => {
                      setCoRaw({});
                      setCoEdit(
                        Object.fromEntries(
                          CO_CREWING_ROWS.map(({ key }) => [
                            key,
                            { ...(MOCK_CHANGEOVER_CREWING[key] ?? { PA: 1, MO: 1, QI: 1 }) },
                          ]),
                        ),
                      );
                      setCoEditing(false);
                    }}
                  />
                </>
              ) : (
                <Button
                  label="Edit"
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  onClick={() => {
                    const raw: Record<string, string> = {};
                    CO_CREWING_ROWS.forEach(({ key }) => {
                      (['PA', 'MO', 'QI'] as const).forEach((role) => {
                        raw[`${key}_${role}`] = String(coEdit[key]?.[role] ?? 0);
                      });
                    });
                    setCoRaw(raw);
                    setCoEditing(true);
                  }}
                />
              )}
            </div>
            <div className={styles.tableScrollWrapper}>
            <table className={styles.simpleTable}>
              <thead>
                <tr>
                  <th>Room Type</th>
                  <th style={{ textAlign: 'right' }}>PA</th>
                  <th style={{ textAlign: 'right' }}>MO</th>
                  <th style={{ textAlign: 'right' }}>QI</th>
                </tr>
              </thead>
              <tbody>
                {CO_CREWING_ROWS.map(({ label, key }) => (
                  <tr key={key}>
                    <td><strong>{label}</strong></td>
                    {(['PA', 'MO', 'QI'] as const).map((role) => (
                      <td key={role} style={{ textAlign: 'right' }}>
                        {coEditing ? (
                          <input
                            type="text"
                            inputMode="decimal"
                            className={styles.editInput}
                            value={coRaw[`${key}_${role}`] ?? ''}
                            onChange={(e) => handleCoChange(`${key}_${role}`, e.target.value)}
                            onBlur={() => handleCoBlur(`${key}_${role}`, key, role)}
                            onFocus={() => { if (coRaw[`${key}_${role}`] === '0') setCoRaw((prev) => ({ ...prev, [`${key}_${role}`]: '' })); }}
                          />
                        ) : (
                          coEdit[key]?.[role] ?? '--'
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </TabPanel>

          {/* ================================================================ */}
          {/* Tab 7 — Holidays                                                 */}
          {/* ================================================================ */}
          <TabPanel header="Holidays" leftIcon="pi pi-calendar mr-2">
            <div className={styles.saveBar} style={{ justifyContent: 'flex-end' }}>
              {holidayEditing ? (
                <>
                  <Button
                    label="Add Holiday"
                    icon="pi pi-plus"
                    size="small"
                    severity="secondary"
                    onClick={handleHolidayAdd}
                  />
                  <Button
                    label="Save"
                    icon="pi pi-check"
                    size="small"
                    style={{ background: '#522d6e', borderColor: '#522d6e' }}
                    onClick={handleHolidaySave}
                  />
                  <Button
                    label="Cancel"
                    icon="pi pi-times"
                    size="small"
                    severity="secondary"
                    onClick={handleHolidayCancel}
                  />
                </>
              ) : (
                <Button
                  label="Edit"
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  onClick={() => { setHolidayOriginal(holidays.map((h) => ({ ...h }))); setHolidayEditing(true); }}
                />
              )}
            </div>

            <div className={styles.tableScrollWrapper}>
            <table className={styles.simpleTable}>
              <thead>
                <tr>
                  <th style={{ minWidth: 150 }}>Date</th>
                  <th style={{ minWidth: 200 }}>Holiday Name</th>
                  {holidayEditing && <th style={{ width: 48 }} />}
                </tr>
              </thead>
              <tbody>
                {holidays
                  .slice()
                  .sort((a, b) => a.date.localeCompare(b.date))
                  .map((h) => (
                  <tr key={h.id}>
                    <td>
                      {holidayEditing ? (
                        <input
                          type="date"
                          className={styles.editInput}
                          value={h.date}
                          onChange={(e) => handleHolidayChange(h.id, 'date', e.target.value)}
                          style={{ width: 145 }}
                        />
                      ) : (
                        h.date
                          ? new Date(h.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td>
                      {holidayEditing ? (
                        <input
                          type="text"
                          className={styles.editInput}
                          value={h.name}
                          placeholder="Holiday name"
                          onChange={(e) => handleHolidayChange(h.id, 'name', e.target.value)}
                          style={{ width: '100%', minWidth: 200 }}
                        />
                      ) : (
                        h.name || <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    {holidayEditing && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => handleHolidayDelete(h.id)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#ef4444', fontSize: 14, padding: '2px 4px',
                          }}
                          title="Delete"
                        >
                          <i className="pi pi-trash" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {holidays.length === 0 && (
                  <tr>
                    <td colSpan={holidayEditing ? 3 : 2} style={{ textAlign: 'center', color: '#94a3b8', padding: '1.5rem' }}>
                      No holidays defined. Click <strong>Add Holiday</strong> to add one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </TabPanel>

          {/* ================================================================ */}
          {/* Tab 8 — BPM by NDC                                               */}
          {/* ================================================================ */}
          <TabPanel header="BPM by NDC" leftIcon="pi pi-bolt mr-2">
            <div style={{ overflowX: 'auto' }}>
              <DataTable
                value={BPM_PREVIEW}
                size="small"
                stripedRows
                showGridlines
                emptyMessage="No BPM data."
              >
                <Column field="ndc"       header="NDC"       sortable style={{ minWidth: '140px' }} />
                <Column field="job_type"  header="Job Type"  sortable style={{ minWidth: '100px' }} />
                <Column field="room_name" header="Room"      sortable style={{ minWidth: '100px' }} />
                <Column field="room_type" header="Room Type" sortable style={{ minWidth: '120px' }} />
                <Column field="bpm"       header="BPM"       sortable style={{ minWidth: '80px' }} />
                <Column field="pa_hc"     header="PA HC"     sortable style={{ minWidth: '80px' }} />
                <Column field="mo_hc"     header="MO HC"     sortable style={{ minWidth: '80px' }} />
                <Column field="qi_hc"     header="QI HC"     sortable style={{ minWidth: '80px' }} />
                <Column field="ebq"       header="EBQ"       sortable style={{ minWidth: '80px' }} />
              </DataTable>
            </div>
          </TabPanel>

        </TabView>
      </div>
    </div>
  );
});

export default CPReferenceTables;
